import { useMemo, useState } from 'react';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Icon } from '@/lib/icon';
import { t } from '@/lib/i18n';
import { usePositionsCatalog, useUnitsCatalog } from '@/features/members';
import { useJobTemplatesList } from '@/features/library';
import { filterByQuery } from '../searchUtils';
import styles from './DiscoverTab.module.css';

const ALL = '';

interface DiscoverTabProps {
  canCreateJob: boolean;
  onRun: (templateId: string) => void;
}

export function DiscoverTab({ canCreateJob, onRun }: DiscoverTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState(ALL);
  const [departmentFilter, setDepartmentFilter] = useState(ALL);

  const templatesList = useJobTemplatesList();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();

  const systemTemplates = useMemo(
    () => (templatesList.data?.items ?? []).filter((t) => t.kind === 'system'),
    [templatesList.data],
  );

  const positionsById = useMemo(() => {
    const map = new Map<string, string>();
    (positionsCatalog.data?.items ?? []).forEach((p) => map.set(p.id, p.name));
    return map;
  }, [positionsCatalog.data]);

  const unitsById = useMemo(() => {
    const map = new Map<string, string>();
    (unitsCatalog.data?.items ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [unitsCatalog.data]);

  const positionOptions = useMemo(
    () =>
      (positionsCatalog.data?.items ?? [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [positionsCatalog.data],
  );

  const departmentOptions = useMemo(
    () =>
      (unitsCatalog.data?.items ?? [])
        .map((u) => ({ id: u.id, name: u.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [unitsCatalog.data],
  );

  const items = useMemo(() => {
    const preFiltered = systemTemplates.filter((item) => {
      if (
        positionFilter !== ALL &&
        item.defaultPositionIds.length > 0 &&
        !item.defaultPositionIds.includes(positionFilter)
      )
        return false;
      if (
        departmentFilter !== ALL &&
        item.defaultUnitIds.length > 0 &&
        !item.defaultUnitIds.includes(departmentFilter)
      )
        return false;
      return true;
    });
    return filterByQuery(preFiltered, searchQuery);
  }, [systemTemplates, positionFilter, departmentFilter, searchQuery]);

  if (templatesList.isLoading) {
    return <p className={styles.loading}>{t('Loading…')}</p>;
  }

  return (
    <div className={styles.tab}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <span aria-hidden="true">🔍</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder={t('Search job templates…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t('Search job templates')}
          />
          {searchQuery ? (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => setSearchQuery('')}
              aria-label={t('Clear search')}
            >
              ×
            </button>
          ) : null}
        </div>

        {departmentOptions.length > 0 ? (
          <SelectFilter
            label={t('Department')}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={departmentOptions}
          />
        ) : null}

        {positionOptions.length > 0 ? (
          <SelectFilter
            label={t('Position')}
            value={positionFilter}
            onChange={setPositionFilter}
            options={positionOptions}
          />
        ) : null}
      </div>

      {items.length > 0 ? (
        <div className={styles.list}>
          {items.map((item) => (
            <div key={item.id} className={styles.row}>
              <Icon iconRef={item.icon} size={20} />
              <div className={styles.rowBody}>
                <span className={styles.rowTitle}>{item.title}</span>
                {item.description ? (
                  <span className={styles.rowDesc}>{item.description}</span>
                ) : null}
                {item.defaultUnitIds.length > 0 || item.defaultPositionIds.length > 0 ? (
                  <div className={styles.rowTags}>
                    {item.defaultUnitIds.map((id) => (
                      <Chip key={id} variant="neutral" size="sm">
                        {unitsById.get(id) ?? id}
                      </Chip>
                    ))}
                    {item.defaultPositionIds.map((id) => (
                      <Chip key={id} variant="neutral" size="sm">
                        {positionsById.get(id) ?? id}
                      </Chip>
                    ))}
                  </div>
                ) : null}
              </div>
              {canCreateJob ? (
                <div className={styles.rowActions}>
                  <Button variant="secondary" size="sm" onClick={() => onRun(item.id)}>
                    {t('Run')}
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.noResults}>{t('No templates match your search or filters.')}</p>
      )}
    </div>
  );
}

interface SelectFilterProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { id: string; name: string }[];
}

function SelectFilter({ label, value, onChange, options }: SelectFilterProps) {
  return (
    <label className={styles.selectFilter}>
      <span className={styles.selectLabel}>{label}</span>
      <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value={ALL}>{t('All')}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
